<template>
  <div class="modal fade" :class="{ 'show': show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog" aria-labelledby="detailedAnalyticsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header bg-gradient-primary text-white">
          <h4 class="modal-title text-white" id="detailedAnalyticsModalLabel">
            <i class="bx bx-line-chart me-2"></i>
            Detailed Performance Analytics - {{ selectedStream.name }}
          </h4>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Analytics Navigation Tabs -->
          <ul class="nav nav-tabs nav-tabs-custom" role="tablist">
            <li class="nav-item">
              <a class="nav-link" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'" data-bs-toggle="tab" href="#overview" role="tab">
                <i class="bx bx-dashboard"></i> Overview
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{ active: activeTab === 'trends' }" @click="activeTab = 'trends'" data-bs-toggle="tab" href="#trends" role="tab">
                <i class="bx bx-trending-up"></i> Trends
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{ active: activeTab === 'zones' }" @click="activeTab = 'zones'" data-bs-toggle="tab" href="#zones" role="tab">
                <i class="bx bx-map"></i> Zone Analysis
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{ active: activeTab === 'users' }" @click="activeTab = 'users'" data-bs-toggle="tab" href="#users" role="tab">
                <i class="bx bx-user"></i> User Performance
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{ active: activeTab === 'forecasting' }" @click="activeTab = 'forecasting'" data-bs-toggle="tab" href="#forecasting" role="tab">
                <i class="bx bx-crystal-ball"></i> Forecasting
              </a>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content p-4">
            <!-- Overview Tab -->
            <div class="tab-pane fade" :class="{ 'show active': activeTab === 'overview' }" id="overview">
              <div class="row">
                <!-- Key Performance Indicators -->
                <div class="col-12 mb-4">
                  <h5 class="text-primary mb-3">
                    <i class="bx bx-stats me-2"></i>Key Performance Indicators
                  </h5>
                  <div class="row">
                    <div class="col-md-3">
                      <div class="card bg-primary text-white">
                        <div class="card-body text-center">
                          <div class="display-6 mb-2">{{ formatNumber(summary.totalRevenue) }}</div>
                          <div class="small">Total Revenue (KES)</div>
                          <div class="progress mt-2" style="height: 4px;">
                            <div class="progress-bar bg-white" :style="{ width: Math.min(100, (summary.totalRevenue / summary.yearlyTarget) * 100) + '%' }"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="card bg-success text-white">
                        <div class="card-body text-center">
                          <div class="display-6 mb-2">{{ summary.achievementRate }}%</div>
                          <div class="small">Target Achievement</div>
                          <div class="mt-2">
                            <i :class="summary.achievementRate >= 100 ? 'bx bx-check-circle' : 'bx bx-time'"></i>
                            {{ summary.achievementRate >= 100 ? 'Target Met' : 'In Progress' }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="card text-white" :class="summary.growthRate >= 0 ? 'bg-info' : 'bg-warning'">
                        <div class="card-body text-center">
                          <div class="display-6 mb-2">{{ summary.growthRate }}%</div>
                          <div class="small">Growth Rate</div>
                          <div class="mt-2">
                            <i :class="summary.growthRate >= 0 ? 'bx bx-trending-up' : 'bx bx-trending-down'"></i>
                            {{ summary.growthRate >= 0 ? 'Growing' : 'Declining' }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="card bg-secondary text-white">
                        <div class="card-body text-center">
                          <div class="display-6 mb-2">{{ formatNumber(summary.totalTransactions) }}</div>
                          <div class="small">Total Transactions</div>
                          <div class="mt-2">
                            <i class="bx bx-receipt"></i>
                            Avg: {{ formatNumber(transactionMetrics.averageTransactionValue) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Performance Insights Deep Dive -->
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="bx bx-time-five text-warning me-2"></i>Peak Performance Analysis
                      </h6>
                    </div>
                    <div class="card-body">
                      <div v-if="performanceInsights.peakHour?.hour">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h4 class="text-warning mb-0">{{ performanceInsights.peakHour.hour }}</h4>
                            <small class="text-muted">Peak Performance Hour</small>
                          </div>
                          <div class="text-end">
                            <div class="text-success">KES {{ formatNumber(performanceInsights.peakHour.revenue) }}</div>
                            <small class="text-muted">{{ performanceInsights.peakHour.transactions }} transactions</small>
                          </div>
                        </div>

                        <!-- Peak Hour Recommendations -->
                        <div class="alert alert-info">
                          <h6 class="alert-heading">Optimization Recommendations</h6>
                          <ul class="mb-0">
                            <li>Deploy additional staff during {{ performanceInsights.peakHour.hour }}</li>
                            <li>Analyze factors contributing to peak performance</li>
                            <li>Replicate peak hour strategies during other periods</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="bx bx-tachometer text-info me-2"></i>Revenue Velocity Analysis
                      </h6>
                    </div>
                    <div class="card-body">
                      <div v-if="performanceInsights.revenueVelocity?.perHour">
                        <div class="row">
                          <div class="col-6">
                            <div class="text-center">
                              <h4 class="text-info mb-0">{{ formatNumber(performanceInsights.revenueVelocity.perHour) }}</h4>
                              <small class="text-muted">KES per Hour</small>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="text-center">
                              <h4 class="text-success mb-0">{{ formatNumber(performanceInsights.revenueVelocity.projected) }}</h4>
                              <small class="text-muted">Projected Total</small>
                            </div>
                          </div>
                        </div>

                        <!-- Velocity Chart -->
                        <div class="mt-3">
                          <div class="progress" style="height: 8px;">
                            <div class="progress-bar bg-gradient-info" :style="{ width: getVelocityPercentage() + '%' }"></div>
                          </div>
                          <div class="d-flex justify-content-between mt-1">
                            <small class="text-muted">Current Pace</small>
                            <small class="text-muted">{{ getVelocityPercentage() }}% of target pace</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Trends Tab -->
            <div class="tab-pane fade" :class="{ 'show active': activeTab === 'trends' }" id="trends">
              <div class="row">
                <div class="col-12 mb-4">
                  <h5 class="text-primary mb-3">
                    <i class="bx bx-trending-up me-2"></i>Performance Trends Analysis
                  </h5>
                </div>

                <!-- Hourly Performance Breakdown (Today only) -->
                <div class="col-12 mb-4" v-if="dateRange.type === 'today' && enhancedInsights.hourlyBreakdown?.length > 0">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="bx bx-time me-2"></i>Hourly Performance Breakdown
                      </h6>
                      <p class="text-muted small mb-0">Revenue and transaction distribution throughout the day</p>
                    </div>
                    <div class="card-body">
                      <div id="hourlyChart" style="height: 300px;"></div>
                    </div>
                  </div>
                </div>

                <!-- Revenue Trend Chart -->
                <div class="col-12 mb-4">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">Revenue Performance Over Time</h6>
                      <p class="text-muted small mb-0">Historical vs Current vs Target comparison</p>
                    </div>
                    <div class="card-body">
                      <div id="trendChart" style="height: 300px;"></div>
                    </div>
                  </div>
                </div>

                <!-- Trend Analysis Cards -->
                <div class="col-md-4">
                  <div class="card text-center">
                    <div class="card-body">
                      <div class="text-success mb-2">
                        <i class="bx bx-trending-up display-4"></i>
                      </div>
                      <h6>Growth Trend</h6>
                      <p class="text-muted">{{ getTrendAnalysis() }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="card text-center">
                    <div class="card-body">
                      <div class="text-info mb-2">
                        <i class="bx bx-calendar display-4"></i>
                      </div>
                      <h6>Seasonality</h6>
                      <p class="text-muted">{{ getSeasonalityAnalysis() }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="card text-center">
                    <div class="card-body">
                      <div class="text-warning mb-2">
                        <i class="bx bx-pulse display-4"></i>
                      </div>
                      <h6>Peak Activity</h6>
                      <p class="text-muted">{{ getPeakActivityAnalysis() }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Zone Analysis Tab -->
            <div class="tab-pane fade" :class="{ 'show active': activeTab === 'zones' }" id="zones">
              <div class="row">
                <div class="col-12 mb-4">
                  <h5 class="text-primary mb-3">
                    <i class="bx bx-map me-2"></i>{{ zoneLabel }} Performance Analysis
                  </h5>
                </div>

                <!-- Zone Performance Matrix -->
                <div class="col-12">
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="card-title mb-0">Zone Performance Matrix</h6>
                        <p class="text-muted small mb-0">
                          Enhanced ranking from API: {{ enhancedInsights.zoneRanking?.length || 0 }} zones analyzed
                        </p>
                      </div>
                      <div class="btn-group btn-group-sm">
                        <button class="btn btn-outline-primary" :class="{ active: zoneViewMode === 'revenue' }" @click="zoneViewMode = 'revenue'">
                          Revenue
                        </button>
                        <button class="btn btn-outline-primary" :class="{ active: zoneViewMode === 'growth' }" @click="zoneViewMode = 'growth'">
                          Growth
                        </button>
                        <button class="btn btn-outline-primary" :class="{ active: zoneViewMode === 'efficiency' }" @click="zoneViewMode = 'efficiency'">
                          Efficiency
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <!-- Enhanced Zone Ranking (if available) -->
                      <div v-if="enhancedInsights.zoneRanking?.length > 0" class="mb-4">
                        <h6 class="text-success mb-3">
                          <i class="bx bx-trophy me-2"></i>Enhanced Zone Rankings (Top 10)
                        </h6>
                        <div class="table-responsive">
                          <table class="table table-hover table-sm">
                            <thead class="table-dark">
                            <tr>
                              <th>Rank</th>
                              <th>{{ zoneLabel }}</th>
                              <th class="text-end">Revenue</th>
                              <th class="text-center">Transactions</th>
                              <th class="text-center">Collectors</th>
                              <th class="text-center">Avg Transaction</th>
                              <th class="text-center">Performance</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="zone in enhancedInsights.zoneRanking" :key="zone.rank"
                                :class="getEnhancedZoneRowClass(zone)">
                              <td>
                                  <span class="badge" :class="zone.rank <= 3 ? 'bg-warning' : 'bg-primary'">
                                    <i v-if="zone.rank === 1" class="bx bx-crown"></i>
                                    #{{ zone.rank }}
                                  </span>
                              </td>
                              <td>
                                <div class="d-flex align-items-center">
                                  <div class="zone-indicator me-2" :class="getEnhancedZoneIndicatorClass(zone)"></div>
                                  <div>
                                    <div class="fw-medium">{{ zone.zone || 'Unassigned' }}</div>
                                    <small class="text-muted">{{ getEnhancedZoneCategory(zone) }}</small>
                                  </div>
                                </div>
                              </td>
                              <td class="text-end">
                                <span class="fw-bold text-success">KES {{ formatNumber(zone.revenue) }}</span>
                              </td>
                              <td class="text-center">{{ formatNumber(zone.transactions) }}</td>
                              <td class="text-center">{{ zone.collectors }}</td>
                              <td class="text-center">
                                <span class="text-info">KES {{ formatNumber(zone.avgTransaction) }}</span>
                              </td>
                              <td class="text-center">
                                  <span class="badge" :class="getEnhancedPerformanceBadgeClass(zone)">
                                    {{ getEnhancedPerformanceLabel(zone) }}
                                  </span>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Standard Zone Performance (fallback) -->
                      <div v-else class="table-responsive">
                        <table class="table table-hover">
                          <thead>
                          <tr>
                            <th>{{ zoneLabel }}</th>
                            <th class="text-end">Current Revenue</th>
                            <th class="text-end">Growth Rate</th>
                            <th class="text-center">Users</th>
                            <th class="text-center">Transactions</th>
                            <th class="text-center">Avg per User</th>
                            <th class="text-center">Performance</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="zone in sortedZonePerformance" :key="zone.zone"
                              :class="getZoneRowClass(zone)">
                            <td>
                              <div class="d-flex align-items-center">
                                <div class="zone-indicator me-2" :class="getZoneIndicatorClass(zone)"></div>
                                <div>
                                  <div class="fw-medium">{{ zone.zone || 'Unassigned' }}</div>
                                  <small class="text-muted">{{ getZoneCategory(zone) }}</small>
                                </div>
                              </div>
                            </td>
                            <td class="text-end">
                              <span class="fw-bold">KES {{ formatNumber(zone.currentRevenue) }}</span>
                            </td>
                            <td class="text-end">
                                <span :class="zone.growth >= 0 ? 'text-success' : 'text-danger'">
                                  <i :class="zone.growth >= 0 ? 'bx bx-up-arrow-alt' : 'bx bx-down-arrow-alt'"></i>
                                  {{ zone.growth }}%
                                </span>
                            </td>
                            <td class="text-center">{{ zone.userCount || 0 }}</td>
                            <td class="text-center">{{ zone.transactionCount || 0 }}</td>
                            <td class="text-center">
                              KES {{ formatNumber(zone.averagePerUser || 0) }}
                            </td>
                            <td class="text-center">
                                <span class="badge" :class="getPerformanceBadgeClass(zone)">
                                  {{ getPerformanceLabel(zone) }}
                                </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- User Performance Tab -->
            <div class="tab-pane fade" :class="{ 'show active': activeTab === 'users' }" id="users">
              <div class="row">
                <div class="col-12 mb-4">
                  <h5 class="text-primary mb-3">
                    <i class="bx bx-user me-2"></i>User Performance Analysis
                  </h5>
                </div>

                <!-- Performance Distribution -->
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">Performance Distribution</h6>
                      <p class="text-muted small mb-0">Top 5 revenue collectors</p>
                    </div>
                    <div class="card-body">
                      <div id="userPerformanceChart" style="height: 250px;"></div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">User Statistics</h6>
                    </div>
                    <div class="card-body">
                      <div class="row text-center">
                        <div class="col-4">
                          <div class="border-end">
                            <h4 class="text-success">{{ userPerformance.topPerformers?.length || 0 }}</h4>
                            <small class="text-muted">Top Performers</small>
                          </div>
                        </div>
                        <div class="col-4">
                          <div class="border-end">
                            <h4 class="text-warning">{{ getAveragePerformers() }}</h4>
                            <small class="text-muted">Average</small>
                          </div>
                        </div>
                        <div class="col-4">
                          <h4 class="text-danger">{{ userPerformance.bottomPerformers?.length || 0 }}</h4>
                          <small class="text-muted">Need Support</small>
                        </div>
                      </div>

                      <hr>

                      <div class="mt-3">
                        <h6>Performance Insights</h6>
                        <ul class="list-unstyled">
                          <li class="mb-2">
                            <i class="bx bx-trophy text-warning me-2"></i>
                            <strong>Top Performer:</strong> {{ performanceInsights.topCollector?.name || 'N/A' }}
                          </li>
                          <li class="mb-2">
                            <i class="bx bx-target-lock text-info me-2"></i>
                            <strong>Average Revenue:</strong> KES {{ formatNumber(getAverageUserRevenue()) }}
                          </li>
                          <li class="mb-2">
                            <i class="bx bx-trending-up text-success me-2"></i>
                            <strong>Improvement Rate:</strong> {{ getImprovementRate() }}%
                          </li>
                          <!-- Enhanced Insights from API -->
                          <li v-if="enhancedInsights.collectorInsights?.avgDailyRevenue" class="mb-2">
                            <i class="bx bx-calendar-day text-primary me-2"></i>
                            <strong>Avg Daily Revenue:</strong> KES {{ formatNumber(enhancedInsights.collectorInsights.avgDailyRevenue) }}
                          </li>
                          <li v-if="enhancedInsights.collectorInsights?.maxDailyRevenue" class="mb-2">
                            <i class="bx bx-trending-up text-success me-2"></i>
                            <strong>Peak Daily Revenue:</strong> KES {{ formatNumber(enhancedInsights.collectorInsights.maxDailyRevenue) }}
                          </li>
                          <li v-if="enhancedInsights.collectorInsights?.totalCollectors" class="mb-2">
                            <i class="bx bx-group text-secondary me-2"></i>
                            <strong>Total Active Collectors:</strong> {{ enhancedInsights.collectorInsights.totalCollectors }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Forecasting Tab -->
            <div class="tab-pane fade" :class="{ 'show active': activeTab === 'forecasting' }" id="forecasting">
              <div class="row">
                <div class="col-12 mb-4">
                  <h5 class="text-primary mb-3">
                    <i class="bx bx-crystal-ball me-2"></i>Revenue Forecasting & Projections
                  </h5>
                </div>

                <!-- Forecast Cards -->
                <div class="col-md-4">
                  <div class="card bg-gradient-primary text-white">
                    <div class="card-body text-center">
                      <i class="bx bx-calendar-week display-4 mb-3"></i>
                      <h6>7-Day Forecast</h6>
                      <h4>KES {{ formatNumber(getWeeklyForecast()) }}</h4>
                      <small>Based on current trends</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="card bg-gradient-success text-white">
                    <div class="card-body text-center">
                      <i class="bx bx-calendar display-4 mb-3"></i>
                      <h6>Monthly Projection</h6>
                      <h4>KES {{ formatNumber(getMonthlyForecast()) }}</h4>
                      <small>{{ getMonthlyForecastConfidence() }} confidence</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="card bg-gradient-info text-white">
                    <div class="card-body text-center">
                      <i class="bx bx-trending-up display-4 mb-3"></i>
                      <h6>Target Achievement</h6>
                      <h4>{{ getTargetAchievementForecast() }}%</h4>
                      <small>Projected by month end</small>
                    </div>
                  </div>
                </div>

                <!-- Forecast Chart -->
                <div class="col-12 mt-4">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">Revenue Forecast Chart</h6>
                      <p class="text-muted small mb-0">Historical data with projected future performance</p>
                    </div>
                    <div class="card-body">
                      <div id="forecastChart" style="height: 300px;"></div>
                    </div>
                  </div>
                </div>

                <!-- Forecast Assumptions -->
                <div class="col-12 mt-4">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="bx bx-info-circle me-2"></i>Forecast Assumptions & Scenarios
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6">
                          <h6>Base Case Scenario</h6>
                          <ul class="list-unstyled">
                            <li><i class="bx bx-check text-success me-2"></i>Current growth rate continues</li>
                            <li><i class="bx bx-check text-success me-2"></i>No major disruptions</li>
                            <li><i class="bx bx-check text-success me-2"></i>Seasonal patterns maintained</li>
                          </ul>
                        </div>
                        <div class="col-md-6">
                          <h6>Risk Factors</h6>
                          <ul class="list-unstyled">
                            <li><i class="bx bx-x text-danger me-2"></i>Economic downturns</li>
                            <li><i class="bx bx-x text-danger me-2"></i>Staff turnover</li>
                            <li><i class="bx bx-x text-danger me-2"></i>Policy changes</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <small class="text-muted">
                <i class="bx bx-time me-1"></i>
                Last updated: {{ getLastUpdated() }}
              </small>
            </div>
            <div>
              <button type="button" class="btn btn-outline-secondary me-2" @click="$emit('close')">
                Close
              </button>
              <button type="button" class="btn btn-success" @click="$emit('export-insights')">
                <i class="bx bx-download me-1"></i>
                Export Full Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'DetailedAnalyticsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    performanceInsights: {
      type: Object,
      default: () => ({})
    },
    enhancedInsights: {
      type: Object,
      default: () => ({
        hourlyBreakdown: [],
        zoneRanking: [],
        collectorInsights: {}
      })
    },
    summary: {
      type: Object,
      default: () => ({})
    },
    zonePerformance: {
      type: Array,
      default: () => []
    },
    userPerformance: {
      type: Object,
      default: () => ({ topPerformers: [], bottomPerformers: [] })
    },
    revenuePerformance: {
      type: Array,
      default: () => []
    },
    trendData: {
      type: Array,
      default: () => []
    },
    categoryBreakdown: {
      type: Array,
      default: () => []
    },
    transactionMetrics: {
      type: Object,
      default: () => ({})
    },
    realTimeMetrics: {
      type: Object,
      default: () => ({})
    },
    selectedStream: {
      type: Object,
      default: () => ({})
    },
    dateRange: {
      type: Object,
      default: () => ({ type: 'today' })
    },
    zoneLabel: {
      type: String,
      default: 'Zones'
    }
  },
  data() {
    return {
      activeTab: 'overview',
      zoneViewMode: 'revenue',
      loadingTrends: false
    }
  },
  computed: {
    sortedZonePerformance() {
      if (!this.zonePerformance) return [];

      const sortKey = this.zoneViewMode === 'revenue' ? 'currentRevenue' :
          this.zoneViewMode === 'growth' ? 'growth' : 'userCount';

      return [...this.zonePerformance].sort((a, b) => {
        return (b[sortKey] || 0) - (a[sortKey] || 0);
      });
    }
  },
  mounted() {
    // Set Highcharts global options to avoid locale issues
    if (typeof window.Highcharts !== 'undefined') {
      window.Highcharts.setOptions({
        lang: {
          thousandsSep: ','
        }
      });
    }
  },
  watch: {
    show(newValue) {
      if (newValue) {
        // When modal opens, render charts for the active tab
        this.$nextTick(() => {
          this.renderChartsForActiveTab();
        });
      }
    },
    activeTab(newTab) {
      // Re-render charts when tab changes
      this.$nextTick(() => {
        this.renderChartsForActiveTab();
      });
    },
    revenuePerformance: {
      handler(newData) {
        console.log('🔍 revenuePerformance data changed:', newData?.length, 'items');
        if (newData?.length > 0) {
          console.log('🔍 Sample revenue data:', newData[0]);
        }
        if (this.show && this.activeTab === 'trends') {
          this.$nextTick(() => {
            this.renderTrendChart();
          });
        }
      },
      deep: true,
      immediate: true
    },
    trendData: {
      handler(newData) {
        console.log('🔍 trendData changed:', newData?.length, 'items');
        if (newData?.length > 0) {
          console.log('🔍 Sample trend data:', newData[0]);
        }
        if (this.show && this.activeTab === 'trends') {
          this.$nextTick(() => {
            this.renderTrendChart();
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab;
    },

    formatNumber(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    getDataAvailabilityInfo() {
      const revenuePoints = this.revenuePerformance?.length || 0;
      const trendPoints = this.trendData?.length || 0;
      const totalPoints = Math.max(revenuePoints, trendPoints);

      if (totalPoints === 0) return 'No trend data available';
      if (totalPoints === 1) return '1 data point (limited trend analysis)';
      return `${totalPoints} data points available for trend analysis`;
    },

    getVelocityPercentage() {
      if (!this.performanceInsights.revenueVelocity?.perHour || !this.selectedStream.monthlyTarget) return 0;
      const targetPerHour = this.selectedStream.monthlyTarget / (30 * 24);
      return Math.min(100, Math.round((this.performanceInsights.revenueVelocity.perHour / targetPerHour) * 100));
    },

    getTrendAnalysis() {
      const growth = this.summary.growthRate || 0;
      if (growth > 20) return 'Strong upward trend with exceptional growth';
      if (growth > 0) return 'Positive growth trend, steady improvement';
      if (growth > -10) return 'Slight decline, monitor closely';
      return 'Significant decline, immediate action required';
    },

    getSeasonalityAnalysis() {
      // Use actual date range and performance data instead of hardcoded month logic
      if (!this.revenuePerformance || this.revenuePerformance.length === 0) {
        return 'Insufficient data for seasonality analysis';
      }

      // Analyze actual performance variance
      const revenues = this.revenuePerformance.map(item => item[3] || 0);
      const avgRevenue = revenues.reduce((sum, rev) => sum + rev, 0) / revenues.length;
      const variance = revenues.reduce((sum, rev) => sum + Math.pow(rev - avgRevenue, 2), 0) / revenues.length;
      const stdDev = Math.sqrt(variance);
      const coefficient = avgRevenue > 0 ? (stdDev / avgRevenue) * 100 : 0;

      if (coefficient > 30) return 'High seasonal variation observed';
      if (coefficient > 15) return 'Moderate seasonal patterns';
      return 'Stable performance, low seasonality';
    },

    getPeakActivityAnalysis() {
      if (this.enhancedInsights.hourlyBreakdown?.length > 0) {
        const maxHour = this.enhancedInsights.hourlyBreakdown.reduce((max, hour) =>
            hour.transactions > max.transactions ? hour : max
        );
        return `Peak at ${maxHour.hourLabel} with ${maxHour.transactions} transactions`;
      }

      if (this.performanceInsights.peakHour?.hour) {
        return `Peak activity at ${this.performanceInsights.peakHour.hour}`;
      }

      return 'Peak analysis requires hourly data';
    },

    // Enhanced zone performance methods
    getEnhancedZoneRowClass(zone) {
      if (zone.revenue === 0) return 'table-danger';
      if (zone.rank === 1) return 'table-warning';
      if (zone.rank <= 3) return 'table-success';
      if (zone.revenue < 10000) return 'table-light';
      return '';
    },

    getEnhancedZoneIndicatorClass(zone) {
      if (zone.rank === 1) return 'bg-warning';
      if (zone.rank <= 3) return 'bg-success';
      if (zone.revenue > 50000) return 'bg-primary';
      if (zone.revenue === 0) return 'bg-danger';
      return 'bg-info';
    },

    getEnhancedZoneCategory(zone) {
      if (zone.rank === 1) return 'Top Performer';
      if (zone.rank <= 3) return 'High Performer';
      if (zone.revenue > 50000) return 'Strong Revenue';
      if (zone.revenue > 20000) return 'Moderate Revenue';
      if (zone.revenue === 0) return 'Inactive';
      return 'Low Revenue';
    },

    getEnhancedPerformanceBadgeClass(zone) {
      if (zone.rank === 1) return 'bg-warning text-dark';
      if (zone.rank <= 3) return 'bg-success';
      if (zone.revenue > 50000) return 'bg-primary';
      if (zone.revenue === 0) return 'bg-danger';
      return 'bg-info';
    },

    getEnhancedPerformanceLabel(zone) {
      if (zone.rank === 1) return 'Champion';
      if (zone.rank <= 3) return 'Excellent';
      if (zone.revenue > 50000) return 'Strong';
      if (zone.revenue > 20000) return 'Good';
      if (zone.revenue === 0) return 'Inactive';
      return 'Needs Focus';
    },

    getZoneRowClass(zone) {
      if (zone.currentRevenue === 0) return 'table-danger';
      if (zone.growth < -10) return 'table-warning';
      if (zone.currentRevenue > 50000) return 'table-success';
      return '';
    },

    getZoneIndicatorClass(zone) {
      if (zone.currentRevenue === 0) return 'bg-danger';
      if (zone.growth < -10) return 'bg-warning';
      if (zone.currentRevenue > 50000) return 'bg-success';
      return 'bg-info';
    },

    getZoneCategory(zone) {
      if (zone.currentRevenue === 0) return 'Inactive';
      if (zone.currentRevenue > 50000) return 'High Revenue';
      if (zone.currentRevenue > 20000) return 'Medium Revenue';
      return 'Low Revenue';
    },

    getPerformanceBadgeClass(zone) {
      if (zone.currentRevenue === 0) return 'bg-danger';
      if (zone.growth < -10) return 'bg-warning';
      if (zone.currentRevenue > 50000 && zone.growth > 0) return 'bg-success';
      return 'bg-info';
    },

    getPerformanceLabel(zone) {
      if (zone.currentRevenue === 0) return 'Inactive';
      if (zone.growth < -10) return 'Declining';
      if (zone.currentRevenue > 50000 && zone.growth > 0) return 'Excellent';
      if (zone.growth > 10) return 'Growing';
      return 'Stable';
    },

    getAveragePerformers() {
      const total = (this.userPerformance.topPerformers?.length || 0) +
          (this.userPerformance.bottomPerformers?.length || 0);
      return Math.max(0, this.summary.activeUsers - total);
    },

    getTopPerformerName() {
      if (this.userPerformance.topPerformers?.length > 0) {
        return this.userPerformance.topPerformers[0].names || 'N/A';
      }
      return 'N/A';
    },

    getAverageUserRevenue() {
      if (!this.summary.activeUsers || !this.summary.totalRevenue) return 0;
      return Math.round(this.summary.totalRevenue / this.summary.activeUsers);
    },

    getImprovementRate() {
      // Calculate improvement rate based on growth
      return Math.max(0, Math.round(this.summary.growthRate || 0));
    },

    getWeeklyForecast() {
      if (!this.summary.totalRevenue) return 0;
      const dailyAverage = this.summary.totalRevenue / this.getDaysInPeriod();
      return Math.round(dailyAverage * 7);
    },

    getMonthlyForecast() {
      if (!this.summary.totalRevenue) return 0;
      const dailyAverage = this.summary.totalRevenue / this.getDaysInPeriod();
      const growthFactor = 1 + (this.summary.growthRate || 0) / 100;
      return Math.round(dailyAverage * 30 * growthFactor);
    },

    getMonthlyForecastConfidence() {
      const dataPoints = this.revenuePerformance?.length || 0;
      if (dataPoints > 10) return 'High';
      if (dataPoints > 5) return 'Medium';
      return 'Low';
    },

    getTargetAchievementForecast() {
      if (!this.selectedStream.monthlyTarget) return 0;
      const forecastRevenue = this.getMonthlyForecast();
      return Math.round((forecastRevenue / this.selectedStream.monthlyTarget) * 100);
    },

    getDaysInPeriod() {
      switch (this.dateRange.type) {
        case 'today': return 1;
        case 'yesterday': return 1;
        case 'this_week': return 7;
        case 'this_month': return 30;
        case 'custom':
          if (this.dateRange.startDate && this.dateRange.endDate) {
            const start = new Date(this.dateRange.startDate);
            const end = new Date(this.dateRange.endDate);
            return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
          }
          return 1;
        default: return 1;
      }
    },

    getLastUpdated() {
      return new Date().toLocaleString();
    },

    refreshTrendData() {
      this.loadingTrends = true;
      this.$emit('fetch-trend-data');

      // Reset loading after 3 seconds regardless
      setTimeout(() => {
        this.loadingTrends = false;
      }, 3000);
    },

    // Chart rendering methods
    renderChartsForActiveTab() {
      if (typeof window.Highcharts === 'undefined') {
        console.warn('Highcharts not available for detailed analytics charts');
        return;
      }

      console.log('🔍 Rendering charts for active tab:', this.activeTab);
      console.log('🔍 Current data status:', {
        revenuePerformance: this.revenuePerformance?.length || 0,
        trendData: this.trendData?.length || 0,
        dateRangeType: this.dateRange?.type
      });

      switch (this.activeTab) {
        case 'trends':
          // Small delay to ensure DOM is ready
          setTimeout(() => {
            this.renderTrendChart();
            if (this.dateRange.type === 'today' && this.enhancedInsights.hourlyBreakdown?.length > 0) {
              this.renderHourlyChart();
            }
          }, 100);
          break;
        case 'users':
          setTimeout(() => {
            this.renderUserPerformanceChart();
          }, 100);
          break;
        case 'forecasting':
          setTimeout(() => {
            this.renderForecastChart();
          }, 100);
          break;
      }
    },

    renderHourlyChart() {
      if (!this.enhancedInsights.hourlyBreakdown || this.enhancedInsights.hourlyBreakdown.length === 0) return;

      const categories = this.enhancedInsights.hourlyBreakdown.map(hour => hour.hourLabel);
      const revenueData = this.enhancedInsights.hourlyBreakdown.map(hour => hour.revenue);
      const transactionData = this.enhancedInsights.hourlyBreakdown.map(hour => hour.transactions);

      window.Highcharts.chart('hourlyChart', {
        chart: {
          type: 'column',
          backgroundColor: 'transparent'
        },
        title: {
          text: 'Hourly Performance Breakdown',
          style: { color: '#6c757d' }
        },
        xAxis: {
          categories: categories,
          title: { text: 'Hour of Day' }
        },
        yAxis: [{
          title: {
            text: 'Revenue (KES)',
            style: { color: '#198754' }
          },
          labels: {
            style: { color: '#198754' },
            formatter: function() {
              return 'KES ' + Math.round(this.value).toLocaleString('en-US');
            }
          }
        }, {
          title: {
            text: 'Transactions',
            style: { color: '#0dcaf0' }
          },
          labels: {
            style: { color: '#0dcaf0' },
            formatter: function() {
              return Math.round(this.value);
            }
          },
          opposite: true
        }],
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function() {
            let s = '<b>' + this.x + '</b><br/>';
            this.points.forEach(point => {
              if (point.series.name === 'Revenue') {
                s += point.series.name + ': <b>KES ' +
                    Math.round(point.y).toLocaleString('en-US') + '</b><br/>';
              } else {
                s += point.series.name + ': <b>' + Math.round(point.y) + '</b><br/>';
              }
            });
            return s;
          }
        },
        series: [{
          name: 'Revenue',
          data: revenueData,
          color: '#198754',
          yAxis: 0
        }, {
          name: 'Transactions',
          data: transactionData,
          color: '#0dcaf0',
          yAxis: 1,
          type: 'line'
        }],
        credits: { enabled: false },
        legend: {
          align: 'center',
          verticalAlign: 'bottom'
        }
      });
    },

    renderTrendChart() {
      console.log('🔍 renderTrendChart called in DetailedAnalyticsModal');
      console.log('🔍 revenuePerformance prop:', this.revenuePerformance?.length, 'items');
      console.log('🔍 trendData prop:', this.trendData?.length, 'items');

      // First check if the chart container exists
      const chartContainer = document.getElementById('trendChart');
      if (!chartContainer) {
        console.log('🔍 Chart container not found, waiting for next tick');
        this.$nextTick(() => {
          this.renderTrendChart();
        });
        return;
      }

      // Use trendData if available and has more data points, otherwise fall back to revenuePerformance
      let chartData = [];
      if (this.trendData?.length > 0) {
        chartData = this.trendData;
        console.log('🔍 Using trendData');
      } else if (this.revenuePerformance?.length > 0) {
        chartData = this.revenuePerformance;
        console.log('🔍 Using revenuePerformance');
      }

      console.log('🔍 Final chartData:', chartData?.length, 'items');
      if (chartData.length > 0) {
        console.log('🔍 Sample data point:', chartData[0]);
      }

      if (!chartData || chartData.length === 0) {
        console.log('🔍 No trend data available - requesting from parent');

        // Emit event to parent to fetch trend data
        this.$emit('fetch-trend-data');

        // Show loading state chart
        window.Highcharts.chart('trendChart', {
          chart: {
            type: 'line',
            backgroundColor: 'transparent'
          },
          title: {
            text: 'Loading Trend Data...',
            style: { color: '#6c757d' }
          },
          subtitle: {
            text: 'Fetching historical performance data'
          },
          xAxis: {
            categories: ['Loading...'],
            title: { text: 'Period' }
          },
          yAxis: {
            title: { text: 'Revenue (KES)' }
          },
          series: [{
            name: 'Loading',
            data: [0],
            color: '#e9ecef'
          }],
          credits: { enabled: false }
        });
        return;
      }

      // If we only have 1 data point, show a message
      if (chartData.length === 1) {
        console.log('🔍 Only 1 data point available - showing single point chart');
        const singlePoint = chartData[0];

        window.Highcharts.chart('trendChart', {
          chart: {
            type: 'column',
            backgroundColor: 'transparent'
          },
          title: {
            text: 'Single Period Performance',
            style: { color: '#6c757d' }
          },
          subtitle: {
            text: 'Historical trends require multiple periods of data'
          },
          xAxis: {
            categories: [singlePoint[0] || 'Current Period'],
            title: { text: 'Period' }
          },
          yAxis: {
            title: { text: 'Revenue (KES)' },
            labels: {
              formatter: function() {
                return 'KES ' + Math.round(this.value).toLocaleString('en-US');
              }
            }
          },
          tooltip: {
            useHTML: true,
            formatter: function() {
              return '<b>' + this.key + '</b><br/>' +
                  this.series.name + ': <b>KES ' + Math.round(this.y).toLocaleString('en-US') + '</b>';
            }
          },
          series: [{
            name: 'Previous Period',
            data: [singlePoint[1] || 0],
            color: '#6c757d'
          }, {
            name: 'Target',
            data: [singlePoint[2] || 0],
            color: '#ffc107'
          }, {
            name: 'Current Period',
            data: [singlePoint[3] || 0],
            color: '#198754'
          }],
          credits: { enabled: false },
          legend: {
            align: 'center',
            verticalAlign: 'bottom'
          }
        });
        return;
      }

      // Multiple data points - render line chart
      const categories = chartData.map((item, index) => {
        if (item[0]) {
          return item[0].substring(0, 10);
        }
        return `Period ${index + 1}`;
      });

      const lastPeriod = chartData.map(item => {
        const value = item[1] || 0;
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      });

      const target = chartData.map(item => {
        const value = item[2] || 0;
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      });

      const currentPeriod = chartData.map(item => {
        const value = item[3] || 0;
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      });

      console.log('🔍 Chart data prepared:', {
        categories: categories,
        lastPeriod: lastPeriod,
        target: target,
        currentPeriod: currentPeriod
      });

      const chartTitle = this.dateRange.type === 'custom' ?
          `${this.dateRange.startDate} to ${this.dateRange.endDate}` :
          `Multi-Period Trend Analysis`;

      try {
        window.Highcharts.chart('trendChart', {
          chart: {
            type: 'line',
            backgroundColor: 'transparent',
            height: 300
          },
          title: { text: null },
          subtitle: {
            text: chartTitle,
            style: { color: '#6c757d' }
          },
          xAxis: {
            categories: categories,
            title: { text: 'Period' },
            labels: {
              rotation: -45,
              style: {
                fontSize: '12px'
              }
            }
          },
          yAxis: {
            title: { text: 'Revenue (KES)' },
            labels: {
              formatter: function() {
                return 'KES ' + Math.round(this.value).toLocaleString('en-US');
              }
            }
          },
          tooltip: {
            shared: true,
            useHTML: true,
            formatter: function() {
              let s = '<b>' + this.x + '</b><br/>';
              this.points.forEach(point => {
                s += point.series.name + ': <b>KES ' +
                    Math.round(point.y).toLocaleString('en-US') + '</b><br/>';
              });
              return s;
            }
          },
          plotOptions: {
            line: {
              dataLabels: { enabled: false },
              enableMouseTracking: true,
              marker: {
                enabled: true,
                radius: 4
              }
            }
          },
          series: [{
            name: 'Previous Period',
            data: lastPeriod,
            color: '#6c757d'
          }, {
            name: 'Target',
            data: target,
            color: '#ffc107',
            dashStyle: 'Dash'
          }, {
            name: 'Current Period',
            data: currentPeriod,
            color: '#198754'
          }],
          credits: { enabled: false },
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal'
          }
        });

        console.log('🔍 Trend chart rendered successfully');
      } catch (error) {
        console.error('🔍 Error rendering trend chart:', error);
      }
    },

    renderUserPerformanceChart() {
      if (!this.userPerformance.topPerformers || this.userPerformance.topPerformers.length === 0) {
        // Show empty state in chart
        window.Highcharts.chart('userPerformanceChart', {
          chart: {
            type: 'column',
            backgroundColor: 'transparent'
          },
          title: {
            text: 'No User Performance Data Available',
            style: { color: '#6c757d' }
          },
          subtitle: {
            text: 'Data will appear when users have transactions in the selected period'
          },
          xAxis: {
            categories: ['No Data']
          },
          yAxis: {
            title: { text: 'Revenue (KES)' }
          },
          series: [{
            name: 'Revenue',
            data: [0],
            showInLegend: false,
            color: '#e9ecef'
          }],
          credits: { enabled: false }
        });
        return;
      }

      const topPerformers = this.userPerformance.topPerformers.slice(0, 5);
      const categories = topPerformers.map(user => {
        // Use actual names from API, with safe fallback
        const name = user.names || `User ${user.idNo}` || 'Unknown';
        return name.split(' ')[0]; // First name only for chart readability
      });
      const revenueData = topPerformers.map(user => user.totalRevenue || 0);

      window.Highcharts.chart('userPerformanceChart', {
        chart: {
          type: 'column',
          backgroundColor: 'transparent'
        },
        title: {
          text: 'Top 5 Performers',
          style: { color: '#6c757d' }
        },
        xAxis: {
          categories: categories,
          title: { text: 'Collectors' }
        },
        yAxis: {
          title: { text: 'Revenue (KES)' },
          labels: {
            formatter: function() {
              return 'KES ' + Math.round(this.value).toLocaleString('en-US');
            }
          }
        },
        tooltip: {
          useHTML: true,
          formatter: function() {
            return '<b>' + this.key + '</b><br/>' +
                'Revenue: <b>KES ' + Math.round(this.y).toLocaleString('en-US') + '</b>';
          }
        },
        plotOptions: {
          column: {
            dataLabels: { enabled: false },
            colorByPoint: true,
            colors: ['#198754', '#17a2b8', '#ffc107', '#fd7e14', '#6f42c1']
          }
        },
        series: [{
          name: 'Revenue',
          data: revenueData,
          showInLegend: false
        }],
        credits: { enabled: false }
      });
    },

    renderForecastChart() {
      // Generate forecast data based on current trends
      const forecastData = this.generateForecastData();

      window.Highcharts.chart('forecastChart', {
        chart: {
          type: 'line',
          backgroundColor: 'transparent'
        },
        title: {
          text: 'Revenue Forecast',
          style: { color: '#6c757d' }
        },
        xAxis: {
          categories: forecastData.categories,
          title: { text: 'Time Period' }
        },
        yAxis: {
          title: { text: 'Revenue (KES)' },
          labels: {
            formatter: function() {
              return 'KES ' + Math.round(this.value).toLocaleString('en-US');
            }
          }
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function() {
            let s = '<b>' + this.x + '</b><br/>';
            this.points.forEach(point => {
              s += point.series.name + ': <b>KES ' +
                  Math.round(point.y).toLocaleString('en-US') + '</b><br/>';
            });
            return s;
          }
        },
        plotOptions: {
          line: {
            dataLabels: { enabled: false }
          }
        },
        series: [{
          name: 'Historical',
          data: forecastData.historical,
          color: '#198754'
        }, {
          name: 'Forecast',
          data: forecastData.forecast,
          color: '#0dcaf0',
          dashStyle: 'Dash'
        }, {
          name: 'Optimistic',
          data: forecastData.optimistic,
          color: '#20c997',
          dashStyle: 'Dot'
        }],
        credits: { enabled: false },
        legend: {
          align: 'center',
          verticalAlign: 'bottom'
        }
      });
    },

    generateForecastData() {
      const categories = [];
      const historical = [];
      const forecast = [];
      const optimistic = [];

      // Generate historical data from actual revenue performance
      if (this.revenuePerformance && this.revenuePerformance.length > 0) {
        this.revenuePerformance.forEach(item => {
          categories.push(item[0]);
          historical.push(item[3]); // Current period data
        });
      }

      // Only generate forecasts if we have actual data
      if (historical.length === 0) {
        return { categories: ['No Data'], historical: [0], forecast: [0], optimistic: [0] };
      }

      // Use actual current revenue and growth rate from API data
      const currentRevenue = historical[historical.length - 1] || 0;
      const actualGrowthRate = (this.summary.growthRate || 0) / 100;

      // Generate future periods based on actual growth patterns
      const numForecastPeriods = Math.min(7, Math.max(3, Math.floor(historical.length / 2)));

      for (let i = 1; i <= numForecastPeriods; i++) {
        const period = this.dateRange.type === 'today' ? `Day +${i}` :
            this.dateRange.type === 'this_week' ? `Week +${i}` :
                this.dateRange.type === 'this_month' ? `Month +${i}` :
                    `Period +${i}`;

        categories.push(period);
        historical.push(null); // No historical data for future

        // Conservative forecast based on actual growth
        const forecastValue = currentRevenue * Math.pow(1 + actualGrowthRate, i);
        // Optimistic scenario (150% of actual growth rate)
        const optimisticValue = currentRevenue * Math.pow(1 + (actualGrowthRate * 1.5), i);

        forecast.push(Math.round(Math.max(0, forecastValue)));
        optimistic.push(Math.round(Math.max(0, optimisticValue)));
      }

      return { categories, historical, forecast, optimistic };
    }
  }
}
</script>
<style scoped>
.modal-xl {
  max-width: 1200px;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-success {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.zone-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.nav-tabs-custom .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  color: #6c757d;
}

.nav-tabs-custom .nav-link.active {
  background: none;
  border-color: #007bff;
  color: #007bff;
}

.nav-tabs-custom .nav-link:hover {
  border-color: #007bff;
  color: #007bff;
}

.display-6 {
  font-size: 2rem;
  font-weight: 600;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.progress {
  height: 8px;
  background-color: rgba(255, 255, 255, 0.2);
}

.progress-bar {
  background-color: rgba(255, 255, 255, 0.8);
}

.alert {
  border: none;
  border-radius: 0.5rem;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.text-gradient {
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>